import React, { useState, useContext } from 'react';
import { Formik, Form } from 'formik';
import { useDispatch } from 'react-redux';
import Skeleton from 'react-loading-skeleton';

import { EditableFieldsContext } from '../context';

import { FieldDisplay } from './utils';

import { FormikTextField } from 'components/fields/FormikTextField';
import { Btn } from 'components/btn/Btn';
import { firstNameValidator, lastNameValidator } from 'common/modules/form-validation/validators';
import { useRootSelector } from 'common/features/featuresReducer';
import { RootState } from 'rootStore';
import { Family } from 'common/api/crm/models/Family';
import { updateUserThunk } from 'common/features/store/duck/home/duck';

export const NameFieldDisplay = ({ isEditing, id }: { isEditing: boolean; id: string }) => {
    const profile = useRootSelector((state: RootState) => state.store.home.homeDetails?.profile);
    const shouldDisable = !profile?.firstName;

    return (
        <FieldDisplay id={id} label="Name" isEditing={isEditing}>
            {profile?.firstName} {profile?.lastName}
            {shouldDisable && <Skeleton className="h-10" />}
        </FieldDisplay>
    );
};

interface NameFieldValues {
    firstName: string;
    lastName: string;
}

export const NameFieldEditComponent = () => {
    const profile = useRootSelector((state: RootState) => state.store.home.homeDetails?.profile);
    const initialValues = {
        firstName: profile?.firstName ?? '',
        lastName: profile?.lastName ?? '',
    };

    const { setFieldToEdit } = useContext(EditableFieldsContext);
    const [errorExists, setErrorExists] = useState(false);
    const dispatch = useDispatch<any>();

    const onSubmit = async (values: NameFieldValues) => {
        const payload = {
            ...profile,
            ...values,
        };

        try {
            await updateUserThunk(payload as Family)(dispatch);
            setFieldToEdit('');
        } catch (e) {
            // renders a common message declaring that it's unable to update information at this point
            setErrorExists(true);
        }
    };

    return (
        <div className="mt-5">
            <Formik initialValues={initialValues} onSubmit={onSubmit}>
                <Form>
                    <div className="flex flex-col sm:flex-row">
                        <div className="mb-5 sm:mr-5">
                            <FormikTextField
                                name="firstName"
                                label="First Name"
                                validate={firstNameValidator}
                            />
                        </div>

                        <div className="mb-5">
                            <FormikTextField
                                name="lastName"
                                label="Last Name"
                                validate={lastNameValidator}
                            />
                        </div>
                    </div>
                    {errorExists && (
                        <p className="text-sm mb-3 text-error">
                            Unable to update your information at this time
                        </p>
                    )}
                    <Btn type="submit">Save</Btn>
                </Form>
            </Formik>
        </div>
    );
};
